import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ItemsService } from 'src/app/items/items.service';
import { ItemPivotModel } from 'src/app/items/item-pivot.model';
import { UtilsService } from 'src/app/shared/utils.service';
import moment from 'moment';
import { ApiRequestService } from '../../../../shared/api-request.service';
import { AppService } from '../../../../app.service';

@Component({
    selector: 'app-contractors-items-edit',
    templateUrl: './contractors-items-edit.component.html',
    styleUrls: ['./contractors-items-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContractorsItemsEditComponent implements OnInit {
  pivot = {} as ItemPivotModel;

  now: moment.Moment = moment();
  expiry: moment.Moment;

  originalExpiryDate: number | null = null;
  expiryChangeNote: string = '';

  filesRemoved: typeof this.pivot.files = [];

  inProgress: boolean = false;

  selectedFiles: File[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) private pivotId: number,
    private itemService: ItemsService,
    private dialogRef: MatDialogRef<ContractorsItemsEditComponent>,
    protected utils: UtilsService,
    private api: ApiRequestService,
    public app: AppService
  ) {}

  ngOnInit() {
    this.getItem();
  }

  getItem() {
    this.itemService
    .findPivot(this.pivotId)
    .subscribe((response) => {
      this.pivot = response.data;
      if ( this.pivot.expiry ) {
        this.expiry = moment.unix(this.pivot.expiry).tz(this.app.account.timezone);
        this.originalExpiryDate = this.pivot.expiry;
      }
    });
  }

  async onSave() {

    if ( this.inProgress ) {
      return;
    }

    // Check if expiry changed and no note/file provided
    const noteProvided = this.expiryChangeNote?.trim().length > 0;
    const fileProvided = this.pivot.files?.length > 0;

    if (this.expiryDateChanged() && !(noteProvided || fileProvided)) {
      this.utils.showToast(
        'You must enter a note or attach a file when changing the expiry date.'
      );
      return;
    }

    this.inProgress = true;

    const pivotCopy = { ...this.pivot } as ItemPivotModel;

    if ( this.expiry ) {
      pivotCopy.expiry = this.toAccountMidnightUnix(this.expiry);
    }

    pivotCopy.expiry_change_note = this.expiryChangeNote?.trim() || null;
    pivotCopy.removed_files = this.filesRemoved;

    this.itemService.updatePivot(this.pivot.id, pivotCopy).then(() => {
      this.utils.showToast('Pre-Qualification Item Updated.');
      this.inProgress = false;
      this.getItem();
    }).catch(() => {
      this.inProgress = false;
      this.utils.showToast('Failed to update the pre-qualification item.');
    });

    this.filesRemoved = [];
    this.expiryChangeNote = '';
  }

  private toAccountMidnightUnix(date: moment.Moment): number {
    return date.clone().tz(this.app.account.timezone, true).startOf('day').unix();
  }

  /**
   * Deletes a selected file after user confirmation and updates the file list.
   *
   * @param {number} id - The unique identifier of the file to be deleted.
   * @param {number} index - The index of the file in the files array to be removed.
   * @param {any} elementRef - A reference to the DOM element used for quick actions display.
   * @return {void} The method does not return a value.
   */
  onDeleteSelectedFiles(id: number, index: number, elementRef: any): void {
    if ( id ) {
      this.utils.showQuickActions(elementRef, 'Are you sure you want to remove the file?', [
        {
          text: 'No',
          handler: (): void => {}
        },
        {
          text: 'Yes',
          handler: (): void => {
            const removedFile = this.pivot.files[index];
            this.api.makeRequest('delete', `v2/file-manager/contractor-pre-qualification-item-pivot/${this.pivot.id}/${id}`)
              .then(async (): Promise<void> => {
                this.filesRemoved.push(removedFile);
                this.pivot.files.splice(index, 1);
                this.utils.showToast('The file was successfully removed.');
                await this.onSave();
              });
          }
        }
      ]);
    }
  }

  onRemoveExpiryDate(evt: any) {
    this.utils.showQuickActions(evt.target, 'Would you like to remove the expiry date? <br>Remember to save after removing it.', [
      {
        text: 'Yes',
        handler: () => {
          this.expiry = null;
          this.pivot.expiry = null;
        }
      },
      {
        text: 'No',
        handler: () => {}
      }
    ]);
  }

  onClose() {
    this.close(true);
  }

  private close(isSuccessful: boolean) {
    this.dialogRef.close(isSuccessful);
  }

  /**
   * Opens the specified URL using the utility function.
   *
   * @param {string} url - The URL to be opened.
   * @return {void} This method does not return a value.
   */
  onOpenUrl(url: string): void {
    this.utils.openUrl(url);
  }

  /**
   * Notifies the user that the URL has been copied to their clipboard via a toast message.
   *
   * @return {void} Does not return any value.
   */
  onCopiedURL(): void {
    this.utils.showToast('The URL is copied to your clipboard.');
  }

  expiryDateChanged(): boolean {
    if ((this.selectedFiles?.length ?? 0) > 0 || (this.pivot.files?.length ?? 0) > 0) {
      return false;
    }

    const newUnix = this.expiry ? this.toAccountMidnightUnix(this.expiry) : null;
    const original = this.originalExpiryDate ?? null;

    return original !== newUnix;
  }

  onSelectedFiles(fileList: any) {
    const files: File[] = Array.from(fileList);
    if ( files && files.length > 0 ) {
      this.selectedFiles.push(...files);
    }
  }
}
